import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import {
  chatConverter,
  messageConverter,
} from "./converters";
import type { Chat, Message, MessageMedia } from "../types";
import { handleSnapshotError } from "../utils/recoveryErrors";
import { reportError } from "../utils/reportError";
import { getScriptedChatVariantOverride } from "../abTesting/scriptedChatVariantOverride";
import type { PostQuizChatMode } from "../abTesting/abTests";
import i18n from "../i18n/config";

const CHATS_COLLECTION = "chats";
const MESSAGES_COLLECTION = "messages";

function isPermissionDeniedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" && code.toLowerCase().includes("permission-denied");
}

function normalizeToTimestamp(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof (value as { toDate?: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate().getTime();
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }
  return 0;
}

function getLatestChatActivity(chat: Chat): number {
  const lastMessages = chat.lastMessages ?? {};
  let latest = 0;

  Object.values(lastMessages).forEach((entry) => {
    const time = normalizeToTimestamp(entry?.createdAt);
    if (time > latest) {
      latest = time;
    }
  });

  if (latest > 0) {
    return latest;
  }

  return (
    normalizeToTimestamp(chat.updatedAt) ||
    normalizeToTimestamp(chat.createdAt)
  );
}

// Chat operations

interface CreateChatRequest {
  characterId: string;
  characterName?: string;
  // Sticky ?ab_scriptedChat= override (see scriptedChatVariantOverride.ts).
  // Only ever populated on dev/staging web builds; the functions side
  // independently ignores it on a production deploy.
  scriptedChatVariantOverride?: "scripted" | "control";
  // The user's active UI language, same value sendChatMessage already sends
  // per-message (see ChatThread.tsx's `i18n.resolvedLanguage ?? i18n.language`).
  // Stamped onto the chat doc server-side (ChatDocument.language) so a
  // scripted-chat opening can be served in German — see
  // apps/functions/src/services/scripted-chat.service.ts.
  language?: "en" | "de";
  // The visitor's `postQuizChatMode` arm (see ../abTesting/abTests.ts), sent
  // only by the onboarding success screen. Persisted verbatim onto the chat
  // doc server-side (ChatDocument.postQuizChatMode) so the experiment is
  // readable from the chat — the "ai" arm writes no scriptState, and `logs`
  // are keyed on a uid that changes at sign-in while chats are migrated.
  postQuizChatMode?: PostQuizChatMode;
}

/**
 * Resolves the active UI language to the two the app supports, for fields
 * that are only ever "en" | "de" (unlike sendChatMessage's `language`, which
 * is passed through more loosely for i18n error strings).
 */
function resolveAppLanguage(): "en" | "de" | undefined {
  const resolved = i18n.resolvedLanguage ?? i18n.language;
  return resolved?.toLowerCase().startsWith("de") ? "de" : resolved ? "en" : undefined;
}

interface CreateChatResponse {
  chatId: string;
  chat: Chat;
}

/**
 * Creates a new chat for a user with a character via Cloud Function.
 */
export async function createChat(
  characterId: string,
  characterName?: string,
  postQuizChatMode?: PostQuizChatMode
): Promise<Chat> {
  const createChatFn = httpsCallable<CreateChatRequest, CreateChatResponse>(
    functions,
    "createChat"
  );

  const scriptedChatVariantOverride = getScriptedChatVariantOverride();
  const language = resolveAppLanguage();

  const result = await createChatFn({
    characterId,
    characterName: characterName?.trim() || undefined,
    ...(scriptedChatVariantOverride ? { scriptedChatVariantOverride } : {}),
    ...(language ? { language } : {}),
    // Only the onboarding success screen passes this, so an ordinary chat
    // started from Explore stays unstamped and the field means exactly
    // "this is a post-quiz first chat, in arm X".
    ...(postQuizChatMode ? { postQuizChatMode } : {}),
  });
  return result.data.chat;
}

/**
 * Gets an existing chat for a character or creates a new one.
 * Checks existing chats first, creates via Cloud Function if not found.
 */
export async function getOrCreateChatForCharacter(
  characterId: string,
  existingChats: Chat[],
  characterName?: string
): Promise<string> {
  const existingChat = existingChats.find(
    (chat) => chat.characterId === characterId
  );

  if (existingChat) {
    return existingChat.id;
  }

  const newChat = await createChat(characterId, characterName);
  return newChat.id;
}

/**
 * Gets a chat by ID.
 */
export async function getChat(chatId: string): Promise<Chat | null> {
  const chatRef = doc(db, CHATS_COLLECTION, chatId).withConverter(chatConverter);
  const snapshot = await getDoc(chatRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data();
}

/**
 * Gets all chats for a user.
 */
export async function getChatsByUserId(userId: string): Promise<Chat[]> {
  const chatsRef = collection(db, CHATS_COLLECTION).withConverter(chatConverter);
  const q = query(
    chatsRef,
    where("userId", "==", userId)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data());
}

/**
 * Gets all chats for a user with a specific character.
 */
export async function getChatsByCharacterId(
  userId: string,
  characterId: string
): Promise<Chat[]> {
  const chatsRef = collection(db, CHATS_COLLECTION).withConverter(chatConverter);
  const q = query(
    chatsRef,
    where("userId", "==", userId),
    where("characterId", "==", characterId)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data());
}

/**
 * Updates the summary of a chat.
 */
export async function updateChatSummary(
  chatId: string,
  summary: string
): Promise<void> {
  const chatRef = doc(db, CHATS_COLLECTION, chatId);
  await updateDoc(chatRef, { summary });
}

/**
 * Updates one or more fields of the denormalized `chat.character` snapshot.
 *
 * This performs a PARTIAL update using Firestore dot-path field masks
 * (`"character.name"`, `"character.age"`, ...) — only the keys present in
 * `fields` are touched; every other key already stored under `character` is
 * left untouched by Firestore.
 *
 * This used to be `updateDoc(chatRef, { character: snapshot })`, which
 * replaced the ENTIRE `character` map on every call. That was a footgun:
 * every caller had to know about and manually re-thread every field it
 * didn't own (e.g. `personalityTraits`, `personaTagline`) just so those
 * fields would survive, or they'd silently get wiped by the next writer.
 * With dot-path updates, a caller that only knows about `name` and
 * `profilePhotoUrl` can update just those two keys without risking data
 * loss on fields owned by other call sites.
 */
export async function updateChatCharacterSnapshot(
  chatId: string,
  fields: Partial<NonNullable<Chat["character"]>>
): Promise<void> {
  const updates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) continue;
    updates[`character.${key}`] = value;
  }

  if (Object.keys(updates).length === 0) {
    return;
  }

  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    await updateDoc(chatRef, updates);
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      return;
    }
    reportError(error, {
      area: "chat",
      surface: "chat_service",
      op: "update_character_snapshot",
      extra: { chatId },
    });
    throw error;
  }
}

export async function updateChatUserDisplayName(
  chatId: string,
  userDisplayName: string
): Promise<void> {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    await updateDoc(chatRef, {
      userDisplayName: userDisplayName.trim(),
      updatedAt: new Date(),
    });
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      return;
    }
    reportError(error, {
      area: "chat",
      surface: "chat_service",
      op: "update_user_display_name",
      extra: { chatId },
    });
    throw error;
  }
}

export async function markChatRead(chatId: string): Promise<void> {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    const now = new Date();
    await updateDoc(chatRef, {
      hasUnread: false,
      lastReadAt: now,
      updatedAt: now,
    });
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      return;
    }
    reportError(error, {
      area: "chat",
      surface: "chat_service",
      op: "mark_chat_read",
      extra: { chatId },
    });
    throw error;
  }
}

// Message operations

/**
 * Creates a new message in a chat.
 */
export async function createMessage(params: {
  chatId: string;
  createdBy: string;
  message: string;
  media?: MessageMedia[];
}): Promise<Message> {
  const messagesRef = collection(db, MESSAGES_COLLECTION).withConverter(messageConverter);

  const now = new Date();
  const messageData: Omit<Message, "id"> = {
    chatId: params.chatId,
    createdAt: now,
    createdBy: params.createdBy,
    message: params.message,
    media: params.media ?? [],
  };

  const docRef = await addDoc(messagesRef, messageData as Message);

  return {
    id: docRef.id,
    ...messageData,
  };
}

/**
 * Gets a message by ID.
 */
export async function getMessage(messageId: string): Promise<Message | null> {
  const messageRef = doc(db, MESSAGES_COLLECTION, messageId).withConverter(messageConverter);
  const snapshot = await getDoc(messageRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data();
}

/**
 * Marks quick replies as dismissed for a message.
 */
export async function dismissMessageQuickReplies(messageId: string): Promise<void> {
  try {
    const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
    await updateDoc(messageRef, { quickReplies: [] });
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      return;
    }
    reportError(error, {
      area: "chat",
      surface: "chat_service",
      op: "dismiss_quick_replies",
      extra: { messageId },
    });
    throw error;
  }
}

/**
 * Gets all messages for a chat, ordered by creation time.
 */
export async function getMessagesByChatId(chatId: string): Promise<Message[]> {
  const messagesRef = collection(db, MESSAGES_COLLECTION).withConverter(messageConverter);
  const q = query(
    messagesRef,
    where("chatId", "==", chatId),
    orderBy("createdAt", "asc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data());
}

/**
 * Gets the most recent messages for a chat with a limit.
 */
export async function getRecentMessages(
  chatId: string,
  messageLimit: number = 50
): Promise<Message[]> {
  const messagesRef = collection(db, MESSAGES_COLLECTION).withConverter(messageConverter);
  const q = query(
    messagesRef,
    where("chatId", "==", chatId),
    orderBy("createdAt", "desc"),
    limit(messageLimit)
  );

  const snapshot = await getDocs(q);
  // Reverse to get chronological order
  return snapshot.docs.map((doc) => doc.data()).reverse();
}

/**
 * Observes all chats for a user in real-time.
 * Returns an unsubscribe function to stop listening.
 */
export function observeUserChats(
  userId: string,
  callback: (chats: Chat[]) => void,
  onError?: (error: Error) => void
): () => void {
  const chatsRef = collection(db, CHATS_COLLECTION).withConverter(chatConverter);
  const q = query(chatsRef, where("userId", "==", userId));

  return onSnapshot(
    q,
    (snapshot) => {
      const chats = snapshot.docs
        .map((doc) => doc.data())
        .sort((a, b) => getLatestChatActivity(b) - getLatestChatActivity(a));
      callback(chats);
    },
    (error) => handleSnapshotError("user_chats", error, onError)
  );
}

/**
 * Observes all messages for a chat in real-time.
 * Returns an unsubscribe function to stop listening.
 */
export function observeMessagesByChatId(
  chatId: string,
  callback: (messages: Message[]) => void,
  onError?: (error: Error) => void
): () => void {
  const messagesRef = collection(db, MESSAGES_COLLECTION).withConverter(messageConverter);
  const q = query(
    messagesRef,
    where("chatId", "==", chatId),
    orderBy("createdAt", "asc")
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs.map((doc) => doc.data());
      callback(messages);
    },
    (error) => handleSnapshotError("chat_messages", error, onError)
  );
}

// Cloud Function operations

interface SendMessageRequest {
  chatId: string;
  message: string;
  media?: MessageMedia[];
  language?: string;
}

interface SendMessageResponse {
  success: boolean;
  userMessageId: string;
  aiMessageId: string;
  tokensRemaining?: number;
  nextAction?: "signup_required_for_media";
}

interface UnlockMediaRequest {
  mediaId: string;
  chatId: string;
  characterId: string;
}

interface UnlockMediaResponse {
  success: boolean;
  unlockedMediaId: string;
  tokensRemaining: number;
}

interface AnimateChatImageRequest {
  chatId: string;
  characterId: string;
  mediaId: string;
  requestId?: string;
}

interface AnimateChatImageResponse {
  success: boolean;
  videoMessageId: string;
  tokensRemaining: number;
  cacheHit: boolean;
  animatedVideoId: string;
}

interface RateChatMediaRequest {
  chatId: string;
  characterId: string;
  mediaId: string;
  mediaType: "photo" | "video";
  vote: "up" | "down";
  downFeedback?: {
    reasonCodes: string[];
    notes?: string;
  };
}

interface RateChatMediaResponse {
  success: boolean;
  mediaId: string;
  mediaType: "photo" | "video";
  userVote: "up" | "down";
  ratingUpCount: number;
  ratingDownCount: number;
}

/**
 * Sends a message to a chat via the Cloud Function.
 * This handles storing the user message and generating an AI response.
 */
export async function sendChatMessage(params: {
  chatId: string;
  message: string;
  media?: MessageMedia[];
  language?: string;
}): Promise<SendMessageResponse> {
  const sendMessageFn = httpsCallable<SendMessageRequest, SendMessageResponse>(
    functions,
    "sendMessage",
    { timeout: 225000 }
  );

  const result = await sendMessageFn({
    chatId: params.chatId,
    message: params.message,
    media: params.media,
    language: params.language,
  });

  return result.data;
}

/**
 * Unlock spicy media by charging tokens.
 */
export async function unlockMedia(
  mediaId: string,
  chatId: string,
  characterId: string
): Promise<UnlockMediaResponse> {
  const unlockMediaFn = httpsCallable<UnlockMediaRequest, UnlockMediaResponse>(
    functions,
    "unlockMedia"
  );

  const result = await unlockMediaFn({
    mediaId,
    chatId,
    characterId,
  });

  return result.data;
}

export async function animateChatImage(params: {
  chatId: string;
  characterId: string;
  mediaId: string;
  requestId?: string;
}): Promise<AnimateChatImageResponse> {
  const animateChatImageFn = httpsCallable<AnimateChatImageRequest, AnimateChatImageResponse>(
    functions,
    "animateChatImage"
  );
  const result = await animateChatImageFn({
    chatId: params.chatId,
    characterId: params.characterId,
    mediaId: params.mediaId,
    requestId: params.requestId,
  });
  return result.data;
}

export async function rateChatMedia(params: {
  chatId: string;
  characterId: string;
  mediaId: string;
  mediaType: "photo" | "video";
  vote: "up" | "down";
  downFeedback?: {
    reasonCodes: string[];
    notes?: string;
  };
}): Promise<RateChatMediaResponse> {
  const rateChatMediaFn = httpsCallable<RateChatMediaRequest, RateChatMediaResponse>(
    functions,
    "rateChatMedia"
  );
  const result = await rateChatMediaFn({
    chatId: params.chatId,
    characterId: params.characterId,
    mediaId: params.mediaId,
    mediaType: params.mediaType,
    vote: params.vote,
    downFeedback: params.downFeedback,
  });
  return result.data;
}
